import {
    BookingEventFilterInput,
    BookingViewDataDocument,
    BookingViewDataQuery,
    BookingViewDataQueryVariables
} from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";
import { copyToClipboard, Roles } from "utilities/app-utilities";
import HTMLCode from "components/common/HTMLCode";
import BookingStatusView from "components/entities/accommodation/booking/views/BookingStatusView";
import Translate from "components/common/Translate";
import If from "components/common/If";
import { Outlet, useParams } from "react-router-dom";
import BookingAdditionalChargesDatalist
    from "../../../components/entities/accommodation/additional-charges/BookingAdditionalChargesDatalist";
import {
    faCalendarDay,
    faCancel,
    faFileInvoiceDollar,
    faInfo,
    faLink,
    faMoneyBill1Wave,
    faPaperclip,
    faPen,
    faRefresh,
    faSms,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/common/confirmation/ConfirmationModal";
import { api, graph } from "../../../config/axios-context";
import ModalPortal from "../../../components/modal/ModalPortal";
import { useEffect, useState } from "react";
import BookingPaidAmountForm from "components/entities/accommodation/booking/form/BookingPaidAmountForm";
import Page, { PageActionsType } from "components/common/page/Page";
import { AppRoutes } from "config/router";
import DeleteConfirmationModal from "components/common/confirmation/DeleteConfirmationModal";
import TabNavLink from "../../../components/common/tabs/TabNavLink";
import TabNav from "../../../components/common/tabs/TabNav";
import AccommodationEventDatalist from "components/entities/accommodation/events/AccommodationEventDatalist";
import NoData from "components/common/NoData";
import Button from "components/common/button/Button";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { SeasonUtilities } from "utilities/entities/app/season-utilities";
import BookingDataView from "components/entities/accommodation/booking/views/BookingDataView";
import bookingUtilities from "utilities/entities/accommodation/booking-utilities";
import BookingCancellationForm from "@/components/entities/accommodation/booking/form/BookingCancellationForm";

const MARK_AS_CANCELLED_MODAL_ID = "booking-view-page-mark-as-cancelled-model";
const MARK_AS_PAID_MODAL_ID = "booking-view-page-mark-as-paid-model";
const BOOKING_VIEW_PAGE_DELETE_MODAL_ID = "booking-view-page-delete-modal";
const BOOKING_VIEW_PAGE_SMS_TEXT_MODAL_ID = "booking-view-page-sms-text-modal";

const BOOKING_VIEW_PAGE_UPDATE_DATA_TRIGGER = "booking-view-page-update-data-trigger";

type Model = NonNullable<BookingViewDataQuery['booking']>;

let requestModel: any = {};

export const BookingViewPageAdditionalCharges = () => {
    const context = useAppContext();
    const params = useParams<{ id: string }>();
    const id = Number(params.id);

    return id ? <BookingAdditionalChargesDatalist
        bookingId={id}
        name="booking-view-page"
        onNewEvent={() => context.sendUpdateEvent(BOOKING_VIEW_PAGE_UPDATE_DATA_TRIGGER)}
    /> : <NoData />
}

export const BookingViewPageEvents = () => {
    const params = useParams<{ id: string }>();
    const id = Number(params.id);
    const fixedFilters: BookingEventFilterInput = {
        bookingId: {
            eq: id
        }
    };
    return id ? <AccommodationEventDatalist bookingId={id} addNew fixedFilters={fixedFilters} name="booking-view-page" /> : <NoData />;
}

export const BookingViewPageView = () => {
    return <div className="w-full lg:w-1/2 xxl:w-1/3">
        <BookingDataView name="booking-view-page" model={requestModel} />
    </div>
}

const BookingViewPage = () => {
    const params = useParams<{ id: string }>();
    const context = useAppContext();
    const [componentState, setComponentState] = useState('loading');
    const [model, setModel] = useState<Model | undefined>(undefined);

    // #region config
    const pageActions: PageActionsType = [
        {
            name: 'refresh',
            title: 'button.reload',
            link: () => loadData().then(),
            icon: faRefresh
        },
        {
            hide: !window.navigator?.clipboard || !model?.guestReservationLinkExtension,
            name: 'guest-link',
            title: "accommodation.booking.guestLink",
            icon: faLink,
            link: async () => {
                const copyRes = await copyToClipboard("https://www.sunrise.house/booking-details?refId=" + model?.guestReservationLinkExtension);
                if (copyRes.success) {
                    context.notify('success', 'common.copy.success');
                } else {
                    context.notify('error', copyRes.error || 'common.copy.error');
                }
            },
        },
        {
            hide: () => model?.isCancelled || !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE),
            name: 'markedAsPaid',
            title: 'accommodation.booking.actions.markAsPaid',
            className: 'text-danger',
            link: () => {
                context.modal.open(MARK_AS_PAID_MODAL_ID);
            },
            icon: faMoneyBill1Wave
        },
        {
            hide: () => model?.isCancelled || !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE),
            name: 'markedAsCancelled',
            title: 'accommodation.booking.actions.markAsCancelled',
            className: 'text-danger',
            link: () => {
                context.modal.open(MARK_AS_CANCELLED_MODAL_ID);
            },
            icon: faCancel
        },
        {
            hide: model?.isCancelled || !navigator.clipboard,
            name: 'getSMSText',
            title: 'accommodation.booking.actions.smsText',
            link: () => {
                context.modal.open(BOOKING_VIEW_PAGE_SMS_TEXT_MODAL_ID);
            },
            icon: faSms
        },
        {
            hide: !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE),
            name: 'edit',
            title: 'button.edit',
            link: () => context.navigate(AppRoutes.ACCOMMODATION_BOOKING_EDIT, { id: params.id! }),
            icon: faPen
        },
        {
            hide: !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE),
            name: 'delete',
            title: 'button.delete',
            className: 'text-danger',
            icon: faTrash,
            link: () => {
                context.modal.open(BOOKING_VIEW_PAGE_DELETE_MODAL_ID, params.id);
            }
        }
    ]

    // #endregion config

    useEffect(() => {
        loadData().then();

        document.getElementById('root')?.addEventListener(BOOKING_VIEW_PAGE_UPDATE_DATA_TRIGGER, loadData);

        return () => {
            document.getElementById('root')?.removeEventListener(BOOKING_VIEW_PAGE_UPDATE_DATA_TRIGGER, loadData);
        }
    }, []);


    const loadData = async () => {
        setComponentState('loading');
        try {
            const response = await graph<BookingViewDataQuery, BookingViewDataQueryVariables>({
                query: BookingViewDataDocument,
                variables: {
                    id: params.id?.toString(),
                    lang: context.language.code
                }
            });

            if (response.errors?.length) {
                throw response.errors;
            }

            if (!response.data?.booking) {
                setComponentState('nodata');
            } else {
                requestModel = response.data.booking;
                setModel(response.data.booking);
                setComponentState('');
            }
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error')
            throw error;
        }
    }

    const view = () => {
        if (!model) return <></>;
        if (model!.isCancelled) {
            return <div className="w-full lg:w-1/2 xxl:w-1/3">
                <BookingDataView name="booking-view-page" model={model!} />
            </div>;
        }
        return (
            <div>
                <TabNav>
                    <TabNavLink
                        to={AppRoutes.ACCOMMODATION_BOOKING_VIEW}
                        params={{ id: params.id }}
                        text='pages.info'
                        icon={faInfo}
                    />
                    <TabNavLink
                        to={AppRoutes.ACCOMMODATION_BOOKING_VIEW_ADDITIONAL_CHARGES}
                        params={{ id: params.id }}
                        text='pages.accommodation.additionalCharges.list'
                        icon={faFileInvoiceDollar}
                    />
                    <TabNavLink
                        to={AppRoutes.ACCOMMODATION_BOOKING_VIEW_EVENTS}
                        params={{ id: params.id }}
                        text='pages.accommodation.event.list'
                        icon={faCalendarDay}
                    />
                    <TabNavLink
                        to={AppRoutes.ACCOMMODATION_BOOKING_VIEW_ATTACHMENTS}
                        params={{ id: params.id }}
                        text={context.t('pages.accommodation.attachments.list')}
                        icon={faPaperclip}
                    />
                </TabNav>
                <Outlet />
            </div>
        );
    };

    const setPaidAmount = async (amount: number) => {
        try {
            const response = await api.patch('booking/PaidAmount', {
                bookingId: Number(params.id),
                amount: amount
            });

            if (!response.success) {
                throw response.data;
            }

            context.modal.close(MARK_AS_PAID_MODAL_ID);
            context.notify('success', 'request.save.success');
            await loadData();
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        }
    }

    const copyText = async (lg: 'en' | 'sr' | 'al') => {
        let textList = [];
        if (!model) return '';

        const inOutDate = `${DateUtil.format(model.inDate, DateFormats.SHORT)} - ${DateUtil.format(model.outDate, DateFormats.SHORT)}`;
        const bkId = `#: BK${SeasonUtilities.formatSeasonName(model.season.name, 'YY')}${model.inSeasonId.toString().padStart(3, '0')}`;

        switch (lg) {
            case 'en':
                textList = [
                    `Reservation confirmation:`,
                    bkId,
                    `Name: ${model?.person.name}`,
                    `Date: ${inOutDate} (${model.nights} nights)`,
                    `Room: ${model.roomType.name}`,
                    `Number of guests: ${model.guestCount}`,
                    model.children ? `Children: ${model.children}` : undefined,
                    `Price: ${model.details.items[0].price}/night (${model.total} EUR) plus city tax`,
                    "",
                    model.guestReservationLinkExtension ? ("Reservation details: sunrise.house/bk/" + model.guestReservationLinkExtension) : undefined,
                    "",
                    "Thank you",
                    "sunrise.house"
                ];
                break;
            case 'sr':
                textList = [
                    `Potvrda rezervacije:`,
                    bkId,
                    `Na ime: ${model?.person.name}`,
                    `Datum: ${inOutDate} (${model.nights} noći)`,
                    `Apartman: ${model.roomType.name}`,
                    `Br gostiju: ${model.guestCount}`,
                    model.children ? `Djeca: ${model.children}` : undefined,
                    `Cijena: ${model.details.items[0].price}/noć (${model.total} EUR) plus boravička taksa`,
                    "",
                    model.guestReservationLinkExtension ? ("Detalji rezervacije: sunrise.house/bk/" + model.guestReservationLinkExtension) : undefined,
                    "",
                    "Hvala",
                    "sunrise.house"
                ];
                break;
            case 'al':
                textList = [
                    `Konfirmimi i rezervimit:`,
                    bkId,
                    `Ne emer: ${model?.person.name}`,
                    `Data: ${inOutDate} (${model.nights} netë)`,
                    `Apartmani: ${model.roomType.name}`,
                    `Nr i musafirve: ${model.guestCount}`,
                    model.children ? `Femijet: ${model.children}` : undefined,
                    `Çmimi: ${model.details.items[0].price}/nata (${model.total} EUR) plus taksa e banimit`,
                    model.guestReservationLinkExtension ? "" : undefined,
                    model.guestReservationLinkExtension ? ("Detajet e rezervimit: sunrise.house/bk/" + model.guestReservationLinkExtension) : undefined,
                    "",
                    "Faleminderit",
                    "sunrise.house"
                ];
                break;
        }

        const text = textList.filter(x => x !== undefined).join("\n");
        if (await copyToClipboard(text)) {
            context.notify('success', 'common.copied');
        }

        context.modal.close(BOOKING_VIEW_PAGE_SMS_TEXT_MODAL_ID);

        return text;
    }

    return (
        <>
            <Page
                title={model ? <>
                    <span><Translate i18nKey="pages.accommodation.booking.view" /></span>
                    <If condition={model.isCancelled || model.hasConflict}>
                        <HTMLCode />
                        <HTMLCode />
                        <BookingStatusView
                            isCancelled={model.isCancelled || false}
                            hasConflict={model.hasConflict || false}
                            outDate={model.outDate || ''}
                            inDate={model.inDate}
                        />
                    </If>
                </> : 'pages.accommodation.booking.view'}
                state={componentState}
                wFull
                actions={pageActions}
            >
                {view()}
            </Page>
            <ModalPortal name={MARK_AS_CANCELLED_MODAL_ID} title="accommodation.booking.actions.markAsCancelled">
                <BookingCancellationForm
                    name={`${MARK_AS_CANCELLED_MODAL_ID}-modal`}
                    bookingId={params.id!}
                    onSuccess={() => {
                        context.modal.close(MARK_AS_CANCELLED_MODAL_ID);
                        loadData();
                    }}
                    onReset={() => {
                        context.modal.close(MARK_AS_CANCELLED_MODAL_ID);
                    }}
                />
            </ModalPortal>
            <ModalPortal name={MARK_AS_PAID_MODAL_ID} title="accommodation.booking.actions.markAsPaid">
                <BookingPaidAmountForm
                    onSubmit={setPaidAmount}
                    additionalAmount={model?.additionalCharge.amountSum || 0}
                    totalAmount={model?.total || 0}
                    paidAmount={model?.paidAmount || 0}
                    bookingId={Number(params.id)}
                />
            </ModalPortal>
            <ModalPortal name={BOOKING_VIEW_PAGE_SMS_TEXT_MODAL_ID} title="accommodation.booking.actions.smsText">
                <div className="w-full grid gap-3 p-3">
                    <Button onClick={() => copyText('en')} title="engilsh">
                        <span>English</span>
                    </Button>
                    <Button onClick={() => copyText('sr')} title="serbian">
                        <span>Serbski</span>
                    </Button>
                    <Button onClick={() => copyText('al')} title="shqip">
                        <span>Shqip</span>
                    </Button>
                </div>
            </ModalPortal>
            <DeleteConfirmationModal
                onConfirm={async () => {
                    await bookingUtilities.deleteBooking(Number(params.id));
                }}
                name={BOOKING_VIEW_PAGE_DELETE_MODAL_ID}
                onAfterConfirmationExecution={() => context.navigate(AppRoutes.ACCOMMODATION_BOOKING_DATALIST, undefined, { replace: true })}
            />
        </>
    );
};

export default BookingViewPage;
